import {
  fetchWithTimeout,
  RequestFailureKind,
  RequestQueryParams,
} from '@amzn/aac-chrome-internal-api';
import { AjaxRequestPublisher } from 'src/commons/utils/metrics';
import composeMetricSlug from './compose-metric-slug';
import logError from './log-error';
import { DEFAULT_ACCEPT_HEADER, REQUEST_TIMEOUT } from './constants';

/*
 * The methods AACChromeUI actually issues. Narrower than fetch's, and the same
 * set AACChromeInternalAPI's REQUEST_METHOD declares -- that value is not
 * re-exported from the package root (only the types are), so the literals are
 * spelled here rather than deep-imported from dist/types.
 */
export type RequestMethod = 'GET' | 'POST' | 'PUT';

export type RequestConfig = {
  url: string;
  method?: RequestMethod;
  // Per-request deadline. Defaults to REQUEST_TIMEOUT (60s), as it did under axios.
  timeout?: number;
  headers?: Record<string, string>;
  // Query parameters; null/undefined values are dropped, matching axios.
  params?: RequestQueryParams;
  // Sent as a JSON body with Content-Type: application/json.
  json?: unknown;
  // A pre-encoded body, for the rare caller that is not sending JSON.
  body?: BodyInit;
  // Defaults to 'include', the fetch equivalent of axios's withCredentials: true.
  credentials?: RequestCredentials;
  /*
   * fetchWithTimeout appends a cache-busting timestamp by DEFAULT. axios added
   * nothing, so this layer defaults it off: turning it on would change the URL
   * every one of these endpoints receives. Pass true deliberately.
   */
  cacheBust?: boolean;
  responseType?: 'json' | 'text' | 'none';
  signal?: AbortSignal;
  /*
   * Attempts allowed for ONE logical request (1 = no retry). Metrics are
   * emitted once for the logical request, not once per attempt -- see below.
   */
  retries?: number;
  /*
   * Publish the per-endpoint Ajax series (`:success`, `:latency`, and the
   * failure counters) and send the failure to the shared logger. Default true.
   *
   * The asyncRequest.tsx wrappers pass false unless the call site opts in,
   * because the call sites behind them went straight to axios and have never
   * published these series. Several of
   * their URLs are supplied by the PAGE rather than fixed in this package
   * (activationAPI, popoverAPI, iconAPI, and the account-switcher ajaxInfo
   * endpoint), and composeMetricSlug keys a series on origin + pathname, so
   * turning them on would mint one metric name per distinct path -- an
   * unbounded set if any of those paths carries an id. Enabling them is a
   * deliberate follow-up that needs a cardinality check on the real URLs, not a
   * side effect of changing transport. Those call sites keep the counters they
   * already publish themselves (AjaxMetricsPublisher, AjaxLandingMetricsPublisher).
   * The fixed-path ones opt in: setLanding always, and the account-switcher
   * endpoint only when it resolves to the same-origin `/ccx/ajax/accounts`.
   */
  publishEndpointMetrics?: boolean;
};

/*
 * A failure surfaced by this layer. fetchWithTimeout rethrows the ORIGINAL
 * error (RedirectError, ForbiddenError, the `Request failed with status N`
 * Error, or fetch's TypeError), so callers keep matching on error identity;
 * these two fields are attached to that same object so a caller can branch on
 * the classification instead of pattern-matching a message whose wording is
 * browser-specific.
 */
export type RequestFailure = Error & {
  kind?: RequestFailureKind;
  status?: number;
};

type CapturedFailure = {
  kind?: RequestFailureKind;
  status?: number;
  // True when this attempt's failure ends the logical request: it was the last
  // attempt, or retrying it could not help.
  terminal?: boolean;
};

/*
 * Only a failure that another attempt might not repeat is retried: the network
 * dropped, the deadline fired, or the server answered 5xx. A 4xx, a 403, a
 * redirect to sign-in, an unparseable body or a request we could not even
 * build fails the same way every time, so retrying it only adds a round trip
 * (and a full timeout per attempt) -- and on a POST that the server already
 * processed and rejected, it would re-send a state-changing request. The axios
 * wrappers retried all of these; that was never deliberate, only
 * indiscriminate.
 */
const isRetryable = ({ kind, status }: CapturedFailure): boolean =>
  kind === 'network' ||
  kind === 'timeout' ||
  (kind === 'http' && status !== undefined && status >= 500);

const attachFailureDetails = (
  error: unknown,
  { kind, status }: CapturedFailure,
): unknown => {
  if (!(error instanceof Error) || kind === undefined) {
    return error;
  }
  const failure = error as RequestFailure;
  failure.kind = kind;
  if (status !== undefined) {
    failure.status = status;
  }
  return failure;
};

/*
 * One attempt.
 *
 * `reportOutcome` decides whether this attempt is allowed to publish the
 * logical request's outcome. A retried request must count as ONE observation:
 * the dashboards compute success rate as SUM(:success) / COUNT(:success), so a
 * request that failed twice and then succeeded would otherwise read as 1 of 3
 * rather than 1 of 1, and its failure counters would be double-counted.
 * Latency stays PER ATTEMPT -- that is what axios published (every `request`
 * call timed its own axios call), and a per-attempt timer is the only one that
 * describes what the network did.
 */
const attempt = <T>(
  config: RequestConfig,
  reportOutcome: boolean,
  captured: CapturedFailure,
): Promise<T> => {
  const {
    url,
    method = 'GET',
    timeout = REQUEST_TIMEOUT,
    credentials = 'include',
    cacheBust = false,
    publishEndpointMetrics = true,
    headers,
    params,
    json,
    body,
    responseType,
    signal,
  } = config;

  const { sanitizedUrl, metricSlug, latencyMetricSlug, successMetricSlug } =
    composeMetricSlug({ endpoint: url, method });

  return fetchWithTimeout<T>(url, timeout, {
    method,
    credentials,
    cacheBust,
    headers: { Accept: DEFAULT_ACCEPT_HEADER, ...headers },
    ...(params !== undefined ? { params } : {}),
    ...(json !== undefined ? { json } : {}),
    ...(body !== undefined ? { body } : {}),
    ...(responseType !== undefined ? { responseType } : {}),
    ...(signal !== undefined ? { signal } : {}),
    emitLatencyMetric: ({ latency }) => {
      /*
       * A caller-cancelled request publishes NOTHING, latency included: its
       * duration is truncated at the moment the caller lost interest and would
       * drag the endpoint's timer down. The axios layer kept an `aborted` flag
       * for exactly this. fetchWithTimeout reports latency from a `finally`, so
       * it fires for a cancellation too; the caller's own signal is what tells
       * the two apart, which is how fetchWithTimeout itself decides. A TIMEOUT
       * still publishes, because it aborts fetchWithTimeout's internal
       * controller rather than this signal -- and it did publish under axios.
       */
      if (publishEndpointMetrics && signal?.aborted !== true) {
        AjaxRequestPublisher.publishTimerMonitor(latencyMetricSlug, latency);
      }
    },
    emitSuccessMetric: ({ isSuccess }) => {
      // A success is terminal, so it always reports. A failure reports only when
      // it is the logical request's outcome -- decided in logError, which
      // fetchWithTimeout calls before the failure's emitSuccessMetric.
      if (publishEndpointMetrics && (isSuccess || captured.terminal === true)) {
        AjaxRequestPublisher.publishCounterMonitor(
          successMetricSlug,
          isSuccess ? 1 : 0,
        );
      }
    },
    logError: ({ error, kind, status }) => {
      // Recorded on EVERY attempt, whatever publishEndpointMetrics says: its
      // presence is how the retry loop below tells a real failure from a caller
      // cancellation, for which fetchWithTimeout reports nothing at all.
      captured.kind = kind;
      captured.status = status;
      captured.terminal = reportOutcome || !isRetryable(captured);
      if (publishEndpointMetrics && captured.terminal) {
        logError({
          error,
          loggerMessage: `Failed ${method} request to '${sanitizedUrl}'`,
          metricSlug,
          ...(kind !== undefined ? { kind } : {}),
          ...(status !== undefined ? { status } : {}),
        });
      }
    },
  });
};

/*
 * Issue one logical request, retrying up to `retries` attempts.
 *
 * Resolves the parsed body directly (fetchWithTimeout's contract) rather than an
 * axios-shaped { data } envelope. Throws on status >= 400, as axios did.
 */
export default async <T>(config: RequestConfig): Promise<T> => {
  const { retries = 1 } = config;
  if (retries < 1) {
    throw new Error('Retry cannot be less than 1');
  }
  let attemptsLeft = retries;
  for (;;) {
    const isLastAttempt = attemptsLeft === 1;
    const captured: CapturedFailure = {};
    try {
      return await attempt<T>(config, isLastAttempt, captured);
    } catch (error) {
      /*
       * No classification means fetchWithTimeout treated this as a cancellation
       * requested through `signal`: the caller no longer wants the response, so
       * it is neither logged, counted, nor retried. Testing this rather than the
       * error's shape matters because a TIMEOUT also rejects with a DOMException
       * named AbortError -- fetchWithTimeout aborts its own controller to
       * enforce the deadline -- and a timeout must still be retried, as it was
       * under axios.
       */
      if (captured.kind === undefined) {
        throw error;
      }
      if (captured.terminal === true) {
        throw attachFailureDetails(error, captured);
      }
      attemptsLeft -= 1;
    }
  }
};
